/* eslint-disable */
import { RoundTableExcel } from 'src/app/domain/company-performance/round/round-table/round-table.component';
import { Round } from 'src/app/domain/company-performance/round/entity/Round';
import { getCell, formats, withCellStyles, dashInsteadOfNullCommon } from '../../utils';
import type { RoundsByCompaniesNames, RoundsByRealization, RoundsTotal, Totals } from './interfaces';
import { Row, Rows } from '../../interfaces';
import { nullToUndefined } from '../../utils/functions';

/**
 * Maps fields from object to new fields for excel.
 *
 * @param data - data to map.
 * @param isAdmin - if true, mapping is for admin, otherwise for viewer.
 * @returns - object with new fields.
 */
// meantime option for admin cancelled but i do not want to delete code meantime
export const getNewMapping = (data: Round, key: string) => getForViewer(data, key);

/**
 * Maps fields from object to new fields for excel.
 *
 * @param data - data to map.
 * @param isAdmin - if true, mapping is for admin, otherwise for viewer.
 * @returns - object with new fields.
 */
// meantime option for admin cancelled but i do not want to delete code meantime
export const getNewTotalMapping = (data: Totals, key: string) => getTotalForViewer(data, key);

/**
 * Get data mapping for viewer.
 *
 * @param data Round to map.
 */
const getTotalForViewer = (data: Totals, key:string) => {
  const body = {
    'Round name': `   ${data.roundName}`,
    'Initial agreement date': '',
    'Conversion date': '',
    'Sale date': '',
    'Investment': getCell({ v: nullToUndefined(data.cost), z: formats.thousandsSeparator, t: 'n' }),
    'Fair value': getCell({ v: nullToUndefined(data.fmv), z: formats.thousandsSeparator, t: 'n' }),
    'Gain/(loss)': getCell({ f: getFormulaForGainLoss(), z: formats.thousandsSeparator, t: 'n' }),
    MOIC: getCell({ f: getFormulaForMoic(), z: formats.multiplier, t: 'n' }),
    'Irr': getCell({ v: nullToUndefined(data.grossIrr), z: formats.percents, t: 'n' }),
    'Fund shares': getCell({ v: nullToUndefined(data.fundShares), z: formats.thousandsSeparator, t: 'n' }),
    'Total shares from this class': getCell({ v: nullToUndefined(data.totalAmountFromThisClass), z: formats.thousandsSeparator, t: 'n' }),
    'Current holding %': getCell({ v: nullToUndefined(data.currentPer), z: formats.percents, t: 'n' }),
    'Original PPS': '',
    'Valuated/sale PPS': '',
    'Pre-money': '',
    'Total round': getCell({ v: nullToUndefined(data.totalRound), z: formats.thousandsSeparator, t: 'n' }),
    'Post-money': '',
    'Holding after round %': '',
    'Advance investment / (commitment to invest)': '',
    'Lead investor': '',
    'Comment': '',
  };
  if (key) {
    body['Company name'] = key;
  }
  currentRowIndex++
  return body;
};


/**
 * Get data mapping for viewer.
 *
 * @param data Round to map.
 */
const getForViewer = (data: Round, key: string) => {
  const body = {
    'Round name': `   ${data.roundName}`,
    'Initial agreement date': dashInsteadOfNullCommon(data.roundDate),
    'Conversion date': data.conversionDate,
    'Sale date': data.saleDate,
    Investment: getCell({ v: nullToUndefined(data.cost), z: formats.thousandsSeparator, t: 'n' }),
    'Fair value': getCell({ v: nullToUndefined(data.fmv), z: formats.thousandsSeparator, t: 'n' }),
    'Gain/(loss)': getCell({
      f:  getFormulaForGainLoss(),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    MOIC: getCell({
       f: getFormulaForMoic(),
       z: formats.multiplier,
       t: 'n' }),
    Irr: getCell({ v: nullToUndefined(data.grossIrr), z: formats.percents, t: 'n' }),
    'Fund shares': getCell({ v: nullToUndefined(data.fundShares), z: formats.thousandsSeparator, t: 'n' }),
    'Total shares from this class': getCell({ v: nullToUndefined(data.totalAmountFromThisClass), z: formats.thousandsSeparator, t: 'n' }),
    'Current holding %': getCell({ v: nullToUndefined(data.currentPer), z: formats.percents, t: 'n' }),
    'Original PPS': getCell({ v: nullToUndefined(data.actualPps), z: formats.toFixed2, t: 'n' }),
    'Valuated/sale PPS': getCell({ v: nullToUndefined(data.valuatedPps), z: formats.toFixed2, t: 'n' }),
    'Pre-money': getCell({ v: nullToUndefined(data.preMoney), z: formats.thousandsSeparator, t: 'n' }),
    'Total round': getCell({ v: nullToUndefined(data.totalRound), z: formats.thousandsSeparator, t: 'n' }),
    'Post-money': getCell({ v: nullToUndefined(data.postMoney), z: formats.thousandsSeparator, t: 'n' }),
    'Holding after round %': getCell({ v: nullToUndefined(data.holdingAfterRound), z: formats.percents, t: 'n' }),
    'Advance investment / (commitment to invest)': getCell({
      v: nullToUndefined(data.commitmentToInvest),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n'
    }),
    'Lead investor': data.leadInvestor,
    Comment: data.comment,
  };
  if (key) {
    body['Company name'] = key;
  }
  currentRowIndex++;
  return body;
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Split rounds by realization.
 *
 * @param data Rounds list.
 * @returns splitted rounds.
 */
export const getRoundsByStatus = (data: Round[]) => data.reduce<RoundsByRealization>((acc, r) => {
  if (r.isRealized) {
    acc.sold.push(r);
  } else {
    acc.active.push(r);
  }
  return acc;
}, { active: [], sold: [] });

const getStatusForTotal = (status: 'active' | 'sold') => (status === 'sold' ? 'realized' : status);


/**
 * Count rounds sums.
 *
 * @param data Rounds list.
 * @returns splited rounds totals.
 */
export const getTotal = (data: RoundTableExcel, key: string): RoundsTotal => {
  const { active, sold } = getRoundsByStatus(data.rounds);
  const getNewTotals = (status: 'active' | 'sold'): Totals => {
    const body = {
      roundName: `Total ${getStatusForTotal(status)}`,
      cost: 0,
      fmv: 0,
      grossIrr: status === 'active' ? data.totalIrr?.active : data.totalIrr?.realized,
      fundShares: 0,
      totalAmountFromThisClass: 0,
      currentPer: 0,
      totalRound: 0,
      tasAfterRound: 0,
      followOnAmount: 0,
      totalInvested: 0,
    };

    if (key) {
      body['Company name'] = key;
    }
    return body;
  };
  const getSumForAcc = (acc: Totals, r: Round) => {
    acc.cost += r.cost;
    acc.fmv += r.fmv;
    acc.fundShares += r.fundShares;
    acc.totalAmountFromThisClass += r.totalAmountFromThisClass;
    acc.currentPer += r.currentPer;
    acc.totalRound += r.totalRound;
    acc.tasAfterRound += r.tasAfterRound;

    return acc;
  };

  const totalActive = active.reduce(getSumForAcc, getNewTotals('active'));
  const totalSold = sold.reduce(getSumForAcc, getNewTotals('sold'));

  return { totalActive, totalSold, totalIrr: data.totalIrr?.total };
};

/**
 * Get total sum of sold and active rounds.
 *
 * @param data Splitted rounds totals.
 * @returns sum of rounds totals
 */
export const getGrandTotal = (data: RoundsTotal, key) => {
  const { totalActive, totalSold } = data;
  const body = {
    roundName: 'Grand total',
    cost: totalActive.cost + totalSold.cost,
    fmv: totalActive.fmv + totalSold.fmv,
    grossIrr: data.totalIrr,
    fundShares: totalActive.fundShares, // Yep, only active should be used
    totalAmountFromThisClass: totalActive.totalAmountFromThisClass, // Yep, only active should be used
    currentPer: totalActive.currentPer, // Yep, only active should be used
    totalRound: totalActive.totalRound + totalSold.totalRound,
    tasAfterRound: totalActive.tasAfterRound, // Yep, only active should be used
    followOnAmount: 0,
    totalInvested: 0,
  };
  if (key) {
    body['Company name'] = key;
  }
  return body;
};

/**
 * Prepares rows for defined rounds.
 *
 * @param data Rounds list.
 * @param headers Table headers.
 * @returns prepared rows for defined rounds.
 */
export const getManyRows = (data: Round[], headers: string[], key: string) => {
  const rows: Rows = [];
  const mappedToHeaders = data.map((d, i) => getNewMapping(d, key));
  if (key) {
    headers = ['Company name'].concat(headers);
  }
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(mapped[header]);
    }
    rows.push(row);
  }

  return rows;
};

/**
 * Prepares row for defined round.
 *
 * @param data Rounds list.
 * @param headers Table headers.
 * @returns prepared row for defined round.
 */
export const getOneRow = (data: Round, headers: string[], key: string) => {
  const mapped = getNewMapping(data, key);
  const row: Row = [];
  if (key) {
    headers = ['Company name'].concat(headers);
  }
  for (const header of headers) {
    row.push(withCellStyles(mapped[header], { font: { bold: true } }));
  }
  return row;
};

/**
 * Prepares row for defined round.
 *
 * @param data Rounds list.
 * @param headers Table headers.
 * @returns prepared row for defined round.
 */
export const getTotalRow = (data: Totals, headers: string[], key: string) => {
  const mapped = getNewTotalMapping(data, key);
  const row: Row = [];
  if (key) {
    headers = ['Company name'].concat(headers);
  }
  for (const header of headers) {
    row.push(withCellStyles(mapped[header], { font: { bold: true } }));
  }
  return row;
};



/**
 * Prepare data for excel export by adding styles, etc.
 *
 * @param data List of all rounds for company.
 * @param headers Headers of the table.
 * @param key signifier for single/multiple companies, TODO: remove in future by refactor this file
 * @returns complete data for excel - rows.
 */
export const getPreparedData = (data: RoundTableExcel, headers: string[], key: string = null): Rows => {
  const { active, sold } = getRoundsByStatus(data.rounds);
  const { totalActive, totalSold } = getTotal(data, key);
  const grandTotal = getGrandTotal({ totalActive, totalSold, totalIrr: data.totalIrr?.total }, key);

  const newData: Rows = [];
  if (!key) {
    newData.push(headers);
  }

  if (active.length !== 0) {
    newData.push([getCell({ v: 'Active', s: { font: { bold: true } } })]);
    currentRowIndex++;
    const activeRows = getManyRows(active, headers, key);
    newData.push(...activeRows);
    const activeTotal = getTotalRow(totalActive, headers, key);
    newData.push(activeTotal);
  }

  if (sold.length !== 0) {
    newData.push([getCell({ v: 'Realized', s: { font: { bold: true } } })]);
    currentRowIndex++;
    const soldRows = getManyRows(sold, headers, key);
    newData.push(...soldRows);
    const soldTotal = getTotalRow(totalSold, headers, key);
    newData.push(soldTotal);
  }

  const isBothActiveAndSoldExist = active.length !== 0 && sold.length !== 0;
  if (isBothActiveAndSoldExist) {
    const grandTotalRow = getTotalRow(grandTotal, headers, key);
    newData.push(grandTotalRow);
  }
  if (isSingleCompany) {
    refresh();
  }
  return newData;
};

export const getPreparedDataForAllCompanies = (data: RoundsByCompaniesNames, headers: string[]): Rows => {
  const newData: Rows = [];
  newData.push(['Company name'].concat(headers));
  isSingleCompany = false;
  const keys = Object.keys(data);
  for (const key of keys) {
    newData.push(...getPreparedData(data[key], headers, key));
    newData.push(null);
    currentRowIndex++;
  }
  refresh()
  return newData;
};

const getFormulaForMoic = (): string => {
  if (isSingleCompany) {
    return `IFERROR(F${currentRowIndex}/E${currentRowIndex}, 0)`;
  }
  return `IFERROR(G${currentRowIndex}/F${currentRowIndex}, 0)`
}

const getFormulaForGainLoss = (): string => {
  if (isSingleCompany) {
   return (`F${currentRowIndex}-E${currentRowIndex}`);
  }
  return `(G${currentRowIndex}-F${currentRowIndex})`
}

const refresh = (): void => {
  currentRowIndex = 2;
  isSingleCompany = true;
}

// signifier of current row index, starts with row 2
let currentRowIndex = 2;
// signifier, indicating whether the logic is intended for exporting a single company or multiplayer mode.
let isSingleCompany = true;
